/**
 * Getting Started · Help & Docs · Tell the Build Team — the three tester actions, in the page
 * header on every page (Tim's FAQ "upper-right corner of the REPSit page header", D03 §A.1;
 * Beta-1 plan P6-01 / P6-10).
 *
 * Responsive: three labelled pills from the `lg` breakpoint up; below that they fold into ONE
 * "Beta" menu that keeps the labels (the earlier icon-only fallback — rocket / ? / envelope —
 * was undecodable, UI review 2026-09-21 §3). On phones the menu trigger is a single icon.
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Button, IconButton, Tooltip, Menu, MenuItem, ListItemIcon, ListItemText,
  useMediaQuery, useTheme,
} from '@mui/material';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import { OPEN_WELCOME_EVENT } from '../../onboarding/onboardingEvents';
import { useDemoRole } from '../../contexts/DemoRoleContext';
import { openFeedback } from '../../config/feedback';

export default function HeaderActions() {
  const navigate = useNavigate();
  const { canViewAs } = useDemoRole();
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down('lg'));
  const phone = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);

  const actions = [
    // P6-10: all beta testers are org admins, so Getting Started shows for them; members see Help + feedback.
    ...(canViewAs ? [{ key: 'start', label: 'Getting started', icon: <RocketLaunchIcon fontSize="small" />, onClick: () => window.dispatchEvent(new Event(OPEN_WELCOME_EVENT)), tour: 'header-getting-started' }] : []),
    { key: 'help', label: 'Help & Docs', icon: <HelpOutlineIcon fontSize="small" />, onClick: () => navigate('/help'), tour: 'header-help' },
    { key: 'feedback', label: 'Tell the Build Team', icon: <EmailOutlinedIcon fontSize="small" />, onClick: () => openFeedback('feedback'), tour: 'header-feedback' },
  ];

  const sx = {
    color: 'inherit', borderColor: 'rgba(255,255,255,0.45)', textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap', px: 1.25,
    '&:hover': { borderColor: 'rgba(255,255,255,0.9)', bgcolor: 'rgba(255,255,255,0.12)' },
  };

  if (compact) {
    const close = () => setAnchorEl(null);
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', mr: 0.5 }} data-tour="header-actions">
        {phone ? (
          <Tooltip title="Beta: Getting started · Help & Docs · Tell the Build Team">
            <IconButton color="inherit" onClick={(e) => setAnchorEl(e.currentTarget)} aria-label="Beta help menu" aria-haspopup="menu">
              <SupportAgentIcon />
            </IconButton>
          </Tooltip>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<SupportAgentIcon fontSize="small" />}
            endIcon={<KeyboardArrowDownIcon fontSize="small" />}
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={sx}
            aria-label="Beta help menu"
            aria-haspopup="menu"
          >
            Beta
          </Button>
        )}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={close}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          MenuListProps={{ dense: true, 'aria-label': 'Beta help' }}
        >
          {actions.map((a) => (
            <MenuItem key={a.key} onClick={() => { close(); a.onClick(); }} data-tour={a.tour}>
              <ListItemIcon>{a.icon}</ListItemIcon>
              <ListItemText primary={a.label} />
            </MenuItem>
          ))}
        </Menu>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1, flexShrink: 0 }} data-tour="header-actions">
      {actions.map((a) => (
        <Button key={a.key} size="small" variant="outlined" startIcon={a.icon} onClick={a.onClick} sx={sx} data-tour={a.tour}>
          {a.label}
        </Button>
      ))}
    </Box>
  );
}
